<script lang="ts">
	//Helpers
	import { isWallDisplay, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Design system components
	import {
		Text,
		DeviceButton,
		ControlsContainer,
		IconButton,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	//Components
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import QuickTimeControl from './QuickTimeControl.svelte';
	import { clear_device_timer_state, convertToSeconds } from 'src/helpers/timer_helpers';

	let className = '';
	export { className as class };

	export let status,
		device: Device,
		component: string,
		isWide = false,
		isInSlot = false,
		useV2Components = false,
		controlName = null,
		deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {};

	let isLoading = false,
		hasTimer = false,
		timerStartedAt = 0,
		timer = { time: undefined, percentage: undefined },
		pinLocked,
		showModal = false;
	let showQuickControlModal = false;

	$: tagline = ison
		? $format('label_action_turn_off', { default: 'turn off' })
		: $format('label_action_turn_on', { default: 'turn on' });

	const id = real_id(device.id);

	$: hasRights = hasControlRights(device);

	const handleTurn = async (flipbackSeconds: number = undefined) => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (pinLocked) return (showModal = true);
		if (isLoading || !status) return;
		isLoading = true;
		const on = !ison,
			params = {
				id: Number(component.split(':')[1]),
				on,
				...(flipbackSeconds && { toggle_after: flipbackSeconds }),
			},
			res: SettingRequest = await gen2Request(device, 'switch.set', params);
		if (res.isok) {
			ison = on;
			status[component].output = on;
			// FE-1790: a manual turn (no `toggle_after`) cancels any running countdown on
			// the device; mirror it in the local cache so the UI timer disappears at once.
			if (!flipbackSeconds) {
				clear_device_timer_state(status, device, { component });
				hasTimer = false;
				timerStartedAt = 0;
				timer = { time: undefined, percentage: undefined };
			}
			devices_status.update_device_status({
				id,
				msg: status,
			});
		} else {
			onSettingsResponse(res, device, {
				success: '',
				danger: $format('error_global'),
			});
		}
		isLoading = false;
	};

	const calculateTimer = () => {
		const now = Math.round(Date.now() / 1000),
			timerStartedAt = status[component]?.timer_started_at || 0,
			duration = status[component]?.timer_duration || 0,
			time = Math.max(0, timerStartedAt + duration - now),
			percentage = (time / duration) * 100;
		timer = { time, percentage };
	};

	$: ison = status?.[component]?.output;
	$: if (status?.[component]?.timer_started_at) {
		hasTimer = true;
		if (timerStartedAt != status[component]?.timer_started_at) {
			calculateTimer();
		}
		timerStartedAt = status[component]?.timer_started_at;
	} else {
		hasTimer = false;
		timerStartedAt = 0;
	}

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const handleEntryClick = (entry) => {
		// Send turn request with the selected timer
		const flipbackSeconds = convertToSeconds('00', entry.min, entry.sec);
		handleTurn(flipbackSeconds);

		// Close the quick control modal
		showQuickControlModal = false;
	};

	const a11yProps = { timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }) };
</script>

{#if status?.[component] != null}
	{#key hasTimer}
		{#if isWide || isInSlot}
			<ControlsContainer
				class={classMap({ 'height-full': isInSlot })}
				color={useV2Components ? 'color-0' : undefined}
			>
				<svelte:fragment slot="first">
					{#if useV2Components}
						<DeviceButtonV2
							id="devicebutton-gwjb73p"
							{isLoading}
							{hasTimer}
							{timer}
							isActive={ison}
							icon="fak fa-solid-power-off-circle-plus"
							aria-label={$format('label_turn_device', { default: 'Turn device' })}
							{a11yProps}
							variant={'primary'}
							on:click={(e) => {
								e.stopPropagation();
								handleTurn();
							}}
							disabled={!status || !hasRights}
							class={className}
							{...deviceButtonV2Props}
							size={isWide ? 'l' : 'm'}
						/>
					{:else}
						<DeviceButton
							id="devicebutton-gwjb73p"
							{isWide}
							{isLoading}
							{hasTimer}
							{timer}
							isActive={ison}
							size={isWide ? 'l' : 'm'}
							icon="fak fa-solid-power-off-circle-plus"
							aria-label={$format('label_turn_device', { default: 'Turn device' })}
							{a11yProps}
							variant={'primary'}
							on:click={(e) => {
								e.stopPropagation();
								handleTurn();
							}}
							disabled={!status || !hasRights}
							class={className}
						/>
					{/if}
					<Text styleType="controls-large" class="mt-s text-bold">
						{capFirstChar(tagline)}
					</Text>
					{#if isInSlot}
						<Text styleType="tiny" class="mt-xs">
							{capFirstChar(
								controlName || $format('prop_turn_add_on', { default: 'Add-on' }),
							)}
						</Text>
					{/if}
				</svelte:fragment>

				<svelte:fragment slot="topRightSlot">
					<!-- Wall display device does not support quick control -->
					<!-- Shared devices do not have access to timer controls -->
					{#if !isWallDisplay(device?.type) && !device.shared}
						<IconButton
							aria-label={$format('label_action_show_quick_control', {
								default: 'Show quick control',
							})}
							shape="circle"
							variant="secondary"
							icon="fak fa-regular-clock-circle-plus"
							on:click={() => {
								showQuickControlModal = true;
							}}
						/>
					{/if}
				</svelte:fragment>
			</ControlsContainer>
		{:else}
			{#if useV2Components}
				<DeviceButtonV2
					id="devicebutton-jgknn2d"
					{isLoading}
					{hasTimer}
					{timer}
					isActive={ison}
					icon="fak fa-solid-power-off-circle-plus"
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant={'primary'}
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status || !hasRights}
					class={className}
					{...deviceButtonV2Props}
					size={isWide ? 'l' : 'm'}
				/>
			{:else}
				<DeviceButton
					id="devicebutton-jgknn2d"
					{isWide}
					{isLoading}
					{hasTimer}
					{timer}
					isActive={ison}
					size={isWide ? 'l' : 'm'}
					icon="fak fa-solid-power-off-circle-plus"
					aria-label={$format('label_turn_device', { default: 'Turn device' })}
					{a11yProps}
					variant={'primary'}
					on:click={(e) => {
						e.stopPropagation();
						handleTurn();
					}}
					disabled={!status || !hasRights}
					class={className}
				/>
			{/if}
			{#if isInSlot}
				<Text styleType="controls-large" class="mt-s text-bold">
					{capFirstChar(tagline)}
				</Text>
				<Text styleType="tiny" class="mt-xs">
					{capFirstChar(controlName || $format('prop_turn_add_on', { default: 'Add-on' }))}
				</Text>
			{/if}
		{/if}
	{/key}
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}

{#if !device.shared}
	<QuickTimeControl
		isOn={ison}
		icon="fak fa-regular-clock-circle-plus"
		bind:showQuickControlModal
		on:select={(e) => handleEntryClick(e.detail)}
	/>
{/if}
